// src/pages/Lounge/Clubs/ClubAbout/ClubAbout.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import Loading from "@/components/Loading/Loading";

import api from "@/utils/axios";
import cls from "./ClubAbout.module.scss";

const floatSlow = {
  hidden: { opacity: 0.5 },
  show: {
    opacity: 1,
    scale: [1, 1.02, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.5 },
  show: {
    opacity: 1,
    scale: [1.01, 1, 1.01],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

const slideInUp = {
  hidden: { opacity: 0, y: 18 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 220, damping: 24, mass: 0.9 },
  },
};

const KV = ({ k, v }) => (
  <div className={cls.kvRow}>
    <span className={cls.k}>{k}</span>
    <span className={cls.v}>{v || "-"}</span>
  </div>
);

/** Prevent <img src=""> warnings */
const SafeImg = ({ as = "img", src, ...rest }) => {
  if (!src) return null;
  const Component = as;
  return <Component src={src} {...rest} />;
};

/** avatar can be {url}, string, or null */
const pickAvatarUrl = (avatar) => {
  if (!avatar) return "";
  if (typeof avatar === "string") return avatar;
  if (typeof avatar === "object" && avatar.url) return avatar.url;
  return "";
};

const ClubAbout = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { clubId } = useParams();

  const isPublic = location.pathname === "/lounge";
  const isFriends = location.pathname === "/lounge/friends-feed";
  const isClubs = location.pathname === "/lounge/clubs-feed";
  const isChat = location.pathname === "/lounge/chat";
  const isSearch = location.pathname === "/lounge/search";

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [memberState, setMemberState] = useState("");
  const [club, setClub] = useState(null);
  const [memberCount, setMemberCount] = useState(0);

  const [clubPartners, setClubPartners] = useState([]);
  const [clubHosts, setClubHosts] = useState([]);
  const [rules, setRules] = useState([]);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      try {
        setLoading(true);
        setErr("");

        const { data } = await api.get(`/v1/lounge/clubs/${clubId}/about`);
        if (!alive) return;

        setMemberState(data?.memberState || "");
        setClub(data?.club || null);
        setMemberCount(Number(data?.memberCount || 0));
        setClubPartners(
          Array.isArray(data?.clubPartners) ? data.clubPartners : [],
        );
        setClubHosts(Array.isArray(data?.clubHosts) ? data.clubHosts : []);
        setRules(Array.isArray(data?.rules) ? data.rules : []);
      } catch (e) {
        if (!alive) return;
        setErr(e?.response?.data?.message || e.message || "Request failed");
      } finally {
        if (alive) setLoading(false);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [clubId]);

  const isGoClubHome =
    memberState === "acceptedToJoinMember" ||
    memberState === "activeClubMember" ||
    memberState === "activeClubHost" ||
    memberState === "activeClubPartner";

  const addressLine = useMemo(() => {
    if (!club) return "";
    const parts = [];
    if (club.clubNo) parts.push(club.clubNo);
    if (club.streetName) parts.push(club.streetName);
    if (club.cityName) parts.push(club.cityName);
    return parts.filter(Boolean).join(", ");
  }, [club]);

  const clubName = club?.clubName || t("clubAbout.loadingTitle");
  const clubAvatarUrl =
    pickAvatarUrl(club?.clubAvatar) || pickAvatarUrl(club?.clubAvatarThumb);

  // background hero
  const mobileOneSrc = pickAvatarUrl(club?.mobileOne);
  const mobileTwoSrc = pickAvatarUrl(club?.mobileTwo);
  const desktopOneSrc = pickAvatarUrl(club?.desktopOne);
  const desktopTwoSrc = pickAvatarUrl(club?.desktopTwo);

  // right half visible + redirects, left half always disabled
  const gate = useMemo(() => {
    const mRight = pickAvatarUrl(club?.mobileHalfRight);
    const dRight = pickAvatarUrl(club?.desktopHalfRight);
    const rightEnabled = Boolean(mRight || dRight);

    const onRight = () => {
      if (isGoClubHome) return navigate(`/lounge/clubs/single/${clubId}/home`);
      return navigate(`/lounge/clubs/single/${clubId}`);
    };

    return { mRight, dRight, rightEnabled, onRight };
  }, [club, clubId, isGoClubHome, navigate]);

  const PersonCard = ({ person, fallbackKeyPrefix }) => {
    const avatarUrl = pickAvatarUrl(person?.avatar);
    const username = person?.username
      ? `@${person.username}`
      : t(`${fallbackKeyPrefix}.unknown`);

    const canView = Boolean(person?.memberId);
    const profileTo = canView
      ? `/lounge/member-profile/${person.memberId}`
      : "#";

    return (
      <div className={cls.partnerCard} data-disabled={!canView}>
        <div className={cls.partnerAvatarWrap}>
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={person?.username || "User"}
              className={cls.partnerAvatar}
              draggable="false"
              loading="lazy"
            />
          ) : (
            <div className={cls.partnerAvatarFallback} />
          )}
        </div>

        <div className={cls.partnerMeta}>
          <div className={cls.partnerName}>{username}</div>
          <div className={cls.partnerHint}>
            {canView
              ? t(`${fallbackKeyPrefix}.tapToView`)
              : t(`${fallbackKeyPrefix}.unavailable`)}
          </div>
        </div>

        <Link
          to={profileTo}
          className={cls.viewBtn}
          aria-disabled={!canView}
          onClick={(e) => {
            if (!canView) e.preventDefault();
          }}
        >
          {t("clubAbout.viewProfile")}
        </Link>
      </div>
    );
  };

  // ✅ Loader gate AFTER hooks (safe)
  if (loading) {
    return (
      <Loading
        fullscreen
        titleKey="loading.title"
        subtitleKey="loading.subtitle"
        showTips
        cycleMs={1400}
      />
    );
  }

  // Error / not found after loading
  if (!club || err) {
    return (
      <div className={cls.page}>
        <div className={cls.pageInner}>
          <p className={cls.heroOverlayErr}>{err || t("clubAbout.notFound")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cls.page}>
      <div className={cls.pageInner}>
        {/* Tabs row */}
        <nav className={cls.tabs}>
          <Link
            to="/lounge"
            className={`${cls.tabButton} ${isPublic ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.public")}
          </Link>
          <Link
            to="/lounge/friends-feed"
            className={`${cls.tabButton} ${isFriends ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.friends")}
          </Link>
          <Link
            to="/lounge/clubs-feed"
            className={`${cls.tabButton} ${isClubs ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.clubs")}
          </Link>
          <Link
            to="/lounge/chat"
            className={`${cls.tabButton} ${isChat ? cls.activeTab : ""}`}
          >
            {t("memberPublicFeed.tabs.chat")}
          </Link>
          <Link
            to="/lounge/search"
            className={`${cls.tabButton} ${
              isSearch ? `${cls.activeTab} ${cls.activeSearchTab}` : ""
            }`}
          >
            🔍 {t("memberPublicFeed.tabs.search")}
          </Link>
        </nav>

        <div className={cls.layout}>
          <div className={cls.rightColumn}>
            {/* Clubs CTA */}
            <section className={cls.card}>
              <div className={cls.cardHeaderRow}>
                <h2 className={cls.cardTitle}>{t("memberClubsFeed.title")}</h2>
              </div>

              <div className={cls.clubActionsRow}>
                <Link
                  to="/lounge/my-clubs"
                  className={`${cls.clubMiniBtn} ${cls.btnMyClubs}`}
                >
                  <span className={cls.icon}>
                    <svg viewBox="0 0 24 24">
                      <path d="M16 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zM8 11c1.66 0 3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V20h14v-3.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V20h6v-3.5c0-2.33-4.67-3.5-7-3.5z" />
                    </svg>
                  </span>
                  {t("memberClubsFeed.cta.myClubs.label")}
                </Link>

                <Link
                  to="/lounge/explore-universe"
                  className={`${cls.clubMiniBtn} ${cls.btnExplore}`}
                >
                  <span className={cls.icon}>
                    <svg viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.19 6.19l-2.47 7.4c-.1.31-.49.31-.59 0l-2.47-7.4a.5.5 0 01.63-.63l7.4 2.47c.31.1.31.49 0 .59l-7.4 2.47a.5.5 0 01-.63-.63l2.47-7.4c.1-.31.49-.31.59 0l2.47 7.4a.5.5 0 01-.63.63l-7.4-2.47c-.31-.1-.31-.49 0-.59l7.4-2.47a.5.5 0 01.63.63z" />
                    </svg>
                  </span>
                  {t("memberClubsFeed.cta.exploreUniverse.label")}
                </Link>

                <Link
                  to="/lounge/search-clubs"
                  className={`${cls.clubMiniBtn} ${cls.btnSearch} ${cls.clubMiniBtnActive}`}
                >
                  <span className={cls.icon}>
                    <svg viewBox="0 0 24 24">
                      <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16a6.471 6.471 0 004.23-1.57l.27.28v.79l5 5L20.49 19l-5-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                    </svg>
                  </span>
                  {t("memberClubsFeed.cta.searchClubs.label")}
                </Link>
              </div>
            </section>

            {/* Hero */}
            <section className={cls.searchHero}>
              <div className={cls.heroStage}>
                <div className={cls.mobileOnly} aria-hidden="true">
                  <SafeImg
                    as={motion.img}
                    className={`${cls.heroImg} ${cls.z0}`}
                    src={mobileOneSrc}
                    alt=""
                    variants={floatSlow}
                    initial="hidden"
                    animate="show"
                  />
                  <SafeImg
                    as={motion.img}
                    className={`${cls.heroImg} ${cls.z1}`}
                    src={mobileTwoSrc}
                    alt=""
                    variants={floatSlowAlt}
                    initial="hidden"
                    animate="show"
                  />

                  <button
                    type="button"
                    className={`${cls.heroHalfBtn} ${cls.leftHalf}`}
                    aria-label="Left gate"
                    disabled
                    data-disabled="true"
                  />

                  <button
                    type="button"
                    className={`${cls.heroHalfBtn} ${cls.rightHalf}`}
                    onClick={gate.onRight}
                    aria-label="Right gate"
                    disabled={!gate.rightEnabled}
                    data-disabled={!gate.rightEnabled}
                  >
                    {gate.mRight ? (
                      <img
                        src={gate.mRight}
                        alt=""
                        className={`${cls.heroHalfImg} ${cls.z3}`}
                        draggable="false"
                      />
                    ) : null}
                  </button>
                </div>

                <div className={cls.desktopOnly} aria-hidden="true">
                  <SafeImg
                    as={motion.img}
                    className={`${cls.heroImg} ${cls.z0}`}
                    src={desktopOneSrc}
                    alt=""
                    variants={floatSlow}
                    initial="hidden"
                    animate="show"
                  />
                  <SafeImg
                    as={motion.img}
                    className={`${cls.heroImg} ${cls.z1}`}
                    src={desktopTwoSrc}
                    alt=""
                    variants={floatSlowAlt}
                    initial="hidden"
                    animate="show"
                  />

                  <button
                    type="button"
                    className={`${cls.heroHalfBtn} ${cls.leftHalf}`}
                    aria-label="Left gate"
                    disabled
                    data-disabled="true"
                  />

                  <button
                    type="button"
                    className={`${cls.heroHalfBtn} ${cls.rightHalf}`}
                    onClick={gate.onRight}
                    aria-label="Right gate"
                    disabled={!gate.rightEnabled}
                    data-disabled={!gate.rightEnabled}
                  >
                    {gate.dRight ? (
                      <img
                        src={gate.dRight}
                        alt=""
                        className={`${cls.heroHalfImg} ${cls.z3}`}
                        draggable="false"
                      />
                    ) : null}
                  </button>
                </div>
              </div>

              {/* Content card */}
              <motion.div
                className={cls.heroCardPushed}
                variants={slideInUp}
                initial="hidden"
                animate="show"
              >
                <div className={cls.heroTopRow}>
                  <div className={cls.avatarWrap}>
                    {clubAvatarUrl ? (
                      <img
                        src={clubAvatarUrl}
                        alt={clubName}
                        className={cls.avatar}
                        draggable="false"
                      />
                    ) : (
                      <div className={cls.avatarFallback} />
                    )}
                  </div>

                  <div className={cls.heroTopMeta}>
                    <span className={cls.searchPromptLabel}>
                      {t("clubAbout.hero.label")}
                    </span>
                    <h2 className={cls.searchPromptTitle}>{clubName}</h2>
                    <p className={cls.heroAddress}>{addressLine}</p>
                  </div>
                </div>

                {club?.clubIntro ? (
                  <p className={cls.searchPromptText}>{club.clubIntro}</p>
                ) : null}

                <p className={cls.searchPromptTextSecondary}>
                  {club?.clubDescription || t("clubAbout.noDesc")}
                </p>

                <div className={cls.statsBar}>
                  <div className={cls.statChip}>
                    <span className={cls.statKey}>
                      {t("clubAbout.pills.members")}
                    </span>
                    <span className={cls.statValue}>{memberCount}</span>
                  </div>

                  <div className={cls.statChip}>
                    <span className={cls.statKey}>
                      {t("clubAbout.pills.status")}
                    </span>
                    <span className={cls.statValue}>
                      {club?.clubStatus || "-"}
                    </span>
                  </div>
                </div>

                <div className={cls.detailCard}>
                  <div className={cls.detailTitle}>
                    {t("clubAbout.partners.title")}
                  </div>

                  {clubPartners?.length ? (
                    <div className={cls.partnerGrid}>
                      {clubPartners.map((p, idx) => (
                        <PersonCard
                          key={p.userId || p.memberId || p.username || idx}
                          person={p}
                          fallbackKeyPrefix="clubAbout.partners"
                        />
                      ))}
                    </div>
                  ) : (
                    <p className={cls.bodyText}>
                      {t("clubAbout.partners.empty")}
                    </p>
                  )}
                </div>

                <div className={cls.detailCard}>
                  <div className={cls.detailTitle}>
                    {t("clubAbout.hosts.title")}
                  </div>

                  {clubHosts?.length ? (
                    <div className={cls.partnerGrid}>
                      {clubHosts.map((p, idx) => (
                        <PersonCard
                          key={p.userId || p.memberId || p.username || idx}
                          person={p}
                          fallbackKeyPrefix="clubAbout.hosts"
                        />
                      ))}
                    </div>
                  ) : (
                    <p className={cls.bodyText}>{t("clubAbout.hosts.empty")}</p>
                  )}
                </div>

                <div className={cls.detailCard}>
                  <div className={cls.detailTitle}>
                    {t("clubAbout.rules.title")}
                  </div>

                  {rules?.length ? (
                    <div className={cls.rulesList}>
                      {rules.map((r, idx) => (
                        <div key={r.ruleId || idx} className={cls.ruleRow}>
                          <div className={cls.ruleTop}>
                            <div className={cls.ruleTitle}>{r.title}</div>
                            <div className={cls.ruleOrder}>
                              {t("clubAbout.rules.sort")}: {r.sortOrder ?? 0}
                            </div>
                          </div>

                          {r.description ? (
                            <p className={cls.ruleDesc}>{r.description}</p>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className={cls.bodyText}>{t("clubAbout.rules.empty")}</p>
                  )}
                </div>
              </motion.div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClubAbout;
